import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Stethoscope, UserRound, Plus, Link2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth, SESSION_KEY } from "@/context/AuthContext";

const announce = (u) => { try { localStorage.setItem(SESSION_KEY, JSON.stringify({ id: u.id, at: Date.now() })); } catch { /* storage unavailable */ } };

/** Same email, both account types: hop with one tap, or add the missing type (same password). */
export function RoleSwitch({ user, tid, onDone }) {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [roles, setRoles] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  useEffect(() => { api.get("/auth/roles").then((r) => setRoles(r.data.roles)).catch(() => setRoles([])); }, []);
  if (!roles || user.is_demo) return null;
  const other = user.role === "clinician" ? "client" : "clinician";
  const has = roles.some((r) => r.role === other);
  const Icon = other === "clinician" ? Stethoscope : UserRound;
  const enter = (u, msg) => {
    setUser(u); announce(u); onDone?.();
    sessionStorage.removeItem("hc_tab"); sessionStorage.removeItem("hc_ctab");
    toast.success(msg);
    navigate(u.role === "clinician" ? "/clinician" : "/client");
  };
  const go = async (path, msg) => {
    setBusy(true);
    try { const { data } = await api.post(path, { role: other }); enter(data, msg); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); setConfirm(false); }
  };
  if (has) {
    return (
      <button onClick={() => go("/auth/switch-role", `Switched to your ${other} account`)} disabled={busy} data-testid={tid(`switch-role-${other}`)}
        className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-left hover:bg-stone-50 disabled:opacity-60">
        <span className={`w-8 h-8 rounded-xl grid place-items-center ${other === "clinician" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}><Icon className="w-4 h-4" /></span>
        <span className="min-w-0"><span className="block text-sm font-semibold text-stone-800">Switch to my {other} account</span><span className="block text-[11px] text-stone-500 truncate">{user.email} · no password needed</span></span>
      </button>
    );
  }
  if (!confirm) {
    return (
      <button onClick={() => setConfirm(true)} data-testid={tid(`add-role-${other}`)} className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-left text-sm font-semibold text-stone-700 hover:bg-stone-50">
        <span className="w-8 h-8 rounded-xl grid place-items-center bg-stone-100"><Plus className="w-4 h-4" /></span> Add my {other} account to this email
      </button>
    );
  }
  if (confirm === "merge") {
    return <MergeForm other={other} tid={tid} busy={busy} setBusy={setBusy} onBack={() => setConfirm(true)} onDone={(u) => { setConfirm(false); enter(u, `Linked — you're now in your ${other} account`); }} />;
  }
  return (
    <div className="mx-2 my-1 rounded-xl border border-amber-200 bg-amber-50/60 p-3 text-xs text-stone-700 space-y-2" data-testid={tid("add-role-confirm")}>
      <p>Do you already have a <strong>{other}</strong> account under a different email?</p>
      <button onClick={() => setConfirm("merge")} data-testid={tid("merge-role-start")} className="w-full amber-btn text-xs py-1.5 inline-flex items-center justify-center gap-1.5"><Link2 className="w-3.5 h-3.5" /> Yes — link it to this email</button>
      <button onClick={() => go("/auth/add-role", `Your ${other} account is ready`)} disabled={busy} className="w-full px-3 py-1.5 rounded-full border border-stone-300 font-semibold bg-white" data-testid={tid("add-role-submit")}>{busy ? "Creating…" : `No — create a new ${other} account`}</button>
      <button onClick={() => setConfirm(false)} className="w-full text-[11px] text-stone-500 hover:text-stone-800" data-testid={tid("add-role-cancel")}>Cancel</button>
    </div>
  );
}

function MergeForm({ other, tid, busy, setBusy, onBack, onDone }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const { data } = await api.post("/auth/merge-role", { email, password }); onDone(data); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="mx-2 my-1 rounded-xl border border-amber-200 bg-amber-50/60 p-3 text-xs text-stone-700 space-y-2" data-testid={tid("merge-role-form")}>
      <p>Sign in to your <strong>{other}</strong> account once. It moves onto this email, keeps all its data, and from then on uses this account's password.</p>
      <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder={`${other} account email`} data-testid={tid("merge-role-email")}
        className="w-full px-3 py-2 rounded-lg border border-stone-300 bg-white text-sm focus:border-amber-600 outline-none" />
      <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Its password" data-testid={tid("merge-role-password")}
        className="w-full px-3 py-2 rounded-lg border border-stone-300 bg-white text-sm focus:border-amber-600 outline-none" />
      <div className="flex gap-2">
        <button type="button" onClick={onBack} className="flex-1 px-3 py-1.5 rounded-full border border-stone-300 font-semibold bg-white" data-testid={tid("merge-role-back")}>Back</button>
        <button type="submit" disabled={busy || !email || !password} className="flex-1 amber-btn text-xs py-1.5 disabled:opacity-50" data-testid={tid("merge-role-submit")}>{busy ? "Linking…" : "Link accounts"}</button>
      </div>
    </form>
  );
}
